import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { trackEvent } from "../lib/analytics";
import { useCurrentProperty } from "../context/PropertyContext";
import { useLanguage } from "../context/LanguageContext";
import { buildWhatsAppMessage } from "../lib/whatsappMessages";

const PHONE = "595981858770"; // +595 981 858770

export const WhatsAppButton = () => {
  const { pathname } = useLocation();
  const { current } = useCurrentProperty();
  const { lang } = useLanguage();

  const onPropertyPage = /^\/(propiedad|property)\//.test(pathname);
  const propertyForMessage = onPropertyPage ? current : null;

  const href = useMemo(() => {
    const url =
      typeof window !== "undefined" && onPropertyPage ? window.location.href : "";
    const msg = buildWhatsAppMessage({
      lang,
      property: propertyForMessage,
      url,
    });
    return `https://wa.me/${PHONE}?text=${encodeURIComponent(msg)}`;
  }, [lang, propertyForMessage, onPropertyPage, pathname]);

  const handleClick = () => {
    trackEvent("click_whatsapp", {
      lang,
      location: pathname,
      has_property_context: !!propertyForMessage,
      property_id: propertyForMessage?.id || undefined,
      property_title: propertyForMessage?.title || undefined,
    });
  };

  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp"
      data-testid="whatsapp-float-button"
      onClick={handleClick}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1.2, duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.95 }}
      className="fixed bottom-6 left-6 z-[80] w-14 h-14 rounded-full flex items-center justify-center shadow-2xl"
      style={{ background: "#25D366", boxShadow: "0 8px 24px rgba(37,211,102,0.45)" }}
    >
      <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-40 animate-ping" />
      <svg viewBox="0 0 32 32" width="28" height="28" fill="#ffffff" aria-hidden="true">
        <path d="M16.003 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.255.59 4.453 1.71 6.39L3.2 28.8l6.553-1.717a12.74 12.74 0 0 0 6.247 1.617h.005c7.06 0 12.795-5.73 12.795-12.8 0-3.42-1.331-6.633-3.749-9.05A12.713 12.713 0 0 0 16.003 3.2zm0 23.36h-.005a10.55 10.55 0 0 1-5.382-1.473l-.385-.23-3.886 1.018 1.038-3.79-.252-.39A10.547 10.547 0 0 1 5.45 16c0-5.83 4.74-10.57 10.557-10.57 2.821 0 5.473 1.099 7.467 3.094a10.487 10.487 0 0 1 3.087 7.482c0 5.83-4.741 10.555-10.558 10.555zm5.778-7.91c-.317-.158-1.876-.926-2.166-1.032-.29-.106-.502-.158-.713.158-.21.317-.819 1.031-1.004 1.243-.185.211-.37.238-.687.08-.317-.159-1.337-.493-2.547-1.572-.941-.84-1.577-1.876-1.762-2.193-.185-.317-.02-.488.139-.646.143-.143.317-.37.476-.555.159-.185.211-.317.317-.529.106-.211.053-.396-.026-.555-.08-.158-.713-1.717-.977-2.351-.257-.617-.518-.534-.713-.544l-.608-.011c-.211 0-.555.08-.846.396-.291.317-1.111 1.085-1.111 2.644 0 1.559 1.137 3.066 1.296 3.278.158.211 2.234 3.41 5.413 4.78.756.327 1.346.522 1.806.668.759.241 1.45.207 1.997.126.61-.091 1.876-.766 2.14-1.506.265-.74.265-1.374.185-1.506-.08-.132-.291-.211-.608-.37z"/>
      </svg>
    </motion.a>
  );
};
